import { useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, User, LogOut } from "lucide-react";
import FilterSelect from "../ui/filter-select";
import { useOrganizationStore } from "../../stores/useOrganizationStore";
import { useAuthStore } from "../../stores/useAuthStore";
import { useSignOut } from "../auth/hooks/useSignOut";
import { getUserDisplayName, getUserInitials } from "../auth/utils/userDisplay";
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import UserRoleBadge from "../auth/components/UserRoleBadge";
import { useCurrentRole } from "../../data/hooks/useCurrentRole";
import { isPlatformAdmin } from "../../lib/rbac";
import { useOutageNotificationStore } from "../../stores/useOutageNotificationStore";
import NotificationPanel from "./NotificationPanel";


// ─── Facility Switcher ────────────────────────────────────────────────────────
// Disabled (but visible) on specific-dashboard routes so the user can see
// which facility the dashboard belongs to without being able to switch.
const FacilitySwitcher = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { organizations, setActiveFacility } = useOrganizationStore();

  const match = pathname.match(/\/organizations\/(\d+)\/facilities\/(\d+)/);
  if (!match) return null;

  const dashboardRouteMatch = pathname.match(/\/custom-dashboards\/([^/]+)(\/edit)?$/);
  const isSpecificDashboard = !!dashboardRouteMatch && dashboardRouteMatch[1] !== "new";

  const orgId = Number(match[1]);
  const currentFacilityId = match[2];

  const activeOrg = organizations.find((o) => o.id === orgId);
  if (!activeOrg?.facilities?.length) return null;

  const facilityOptions = activeOrg.facilities.map((f) => ({
    value: String(f.id),
    label: f.name,
  }));

  // currentFacilityId (from the URL) won't match any option here if that
  // facility isn't in this org's active list anymore — e.g. a stale/bookmarked
  // link to one that's since been deactivated. Rather than hide the switcher
  // (which strands the user on the deactivated facility's page with no way
  // back besides hand-editing the URL), leave it open: show a placeholder
  // that tells them why nothing's selected, but still let them expand it and
  // pick one of the org's active facilities. The page itself shows the real
  // "this facility has been deactivated" message alongside it.
  const isCurrentFacilityActive = facilityOptions.some(
    (option) => option.value === currentFacilityId,
  );

  const handleFacilityChange = (val: string) => {
    setActiveFacility(Number(val));
    const newPath = pathname.replace(
      /\/facilities\/\d+/,
      `/facilities/${val}`,
    );
    navigate(newPath);
  };

  return (
    <FilterSelect
      value={isCurrentFacilityActive ? currentFacilityId : undefined}
      onChange={isSpecificDashboard ? () => { } : handleFacilityChange}
      options={facilityOptions}
      placeholder={isCurrentFacilityActive ? undefined : "Select Active Facility"}
      size="xl"
      showIndicator
      disabled={isSpecificDashboard}
    />
  );
};

// ─── Header ───────────────────────────────────────────────────────────────────
export default function Header() {
  const navigate = useNavigate();
  const { signOut } = useSignOut();
  const user = useAuthStore((state) => state.user);
  const role = useCurrentRole();
  const unreadNotificationCount = useOutageNotificationStore(
    (s) => s.entries.filter((entry) => !entry.read).length,
  );

  // ── control popover open state so we can close it
  // programmatically when the user navigates to My Profile ──────────────────
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Separate from the profile popover above — closed explicitly after
  // "View outage" navigation (JAA-1096), not just by the usual click-outside.
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  const displayName = useMemo(() => getUserDisplayName(user), [user]);
  const initials = getUserInitials(displayName);

  // When there's no name the email already is the display name — don't
  // repeat it on the line below.
  const secondaryEmail =
    typeof user?.email === "string" && user.email.trim() !== displayName
      ? user.email
      : null;

  const userRole = useMemo(() => {
    return (user?.role as string) || null;
  }, [user]);

  const onLogout = async (): Promise<void> => {
    setPopoverOpen(false);
    await signOut();
  };

  const handleNavigateToProfile = () => {
    setPopoverOpen(false); // ← close dropdown before navigating
    navigate("/profile");
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 h-[68px] flex items-center px-6">
      <div className="w-full flex items-center justify-end gap-3">
        <FacilitySwitcher />

        {/* Bell — admin-only (§6.3 of the FE push architecture spec); badge
            shows the real unread count (JAA-1062's useOutageNotificationStore),
            not a static placeholder or plain dot. */}
        {isPlatformAdmin(role) && (
          <Popover open={notificationPanelOpen} onOpenChange={setNotificationPanelOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="notification-bell"
                className="relative p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <Bell className="w-5 h-5" />
                {unreadNotificationCount > 0 && (
                  <span
                    data-testid="notification-bell-count"
                    className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white"
                  >
                    {unreadNotificationCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[360px] p-0 !border !border-[#D5D7DA] !outline-none ring-0 !bg-card rounded-[8px] shadow-[0px_0px_20px_rgba(10,13,18,0.05)]"
            >
              <NotificationPanel onNavigate={() => setNotificationPanelOpen(false)} />
            </PopoverContent>
          </Popover>
        )}

        <div className="w-px h-5 bg-gray-200" />

        {/* Profile avatar + popover */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex items-center gap-2 cursor-pointer focus:outline-none"
            >
              <div className="flex size-8 items-center justify-center rounded-full bg-[#1876F214] text-[12px] font-semibold text-[#1876F2]">
                {initials || <User className="size-4" aria-hidden="true" />}
              </div>
              <ChevronDown className="size-4 text-gray-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-[224px] p-0 !border-transparent !outline-none ring-0 !bg-card rounded-[8px] !shadow-none"
          >
            <Card className="w-[224px] !border !border-[#D5D7DA] rounded-[8px] shadow-[0px_0px_20px_rgba(10,13,18,0.05)] overflow-hidden !outline-none ring-0 focus:outline-none">
              {displayName || userRole ? (
                <div className="px-4 pt-4 pb-3 space-y-3">
                  {displayName ? (
                    <div>
                      <p
                        className="font-sans text-[14px] font-semibold leading-[21px] text-left text-[#181D27] truncate"
                        title={displayName}
                      >
                        {displayName}
                      </p>
                      {secondaryEmail ? (
                        <p
                          className="font-sans text-[12px] font-normal leading-[18px] text-left text-[#717680] truncate"
                          title={secondaryEmail}
                        >
                          {secondaryEmail}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {userRole ? (
                    <div className="flex justify-start">
                      <UserRoleBadge role={userRole} />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {displayName || userRole ? (
                <div className="border-t border-[#D5D7DA]" />
              ) : null}

              <div className="p-2 space-y-1">
                <Button
                  variant="ghost"
                  className="w-full justify-start h-9 px-3 text-[14px] font-normal leading-[21px] text-[#181D27] hover:bg-[#eef4fe] hover:text-[#1876F2] transition-colors cursor-pointer"
                  onClick={handleNavigateToProfile}
                >
                  <User className="size-4" />
                  <span className="ml-2">Profile</span>
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-9 px-3 text-[14px] font-normal leading-[21px] text-[#D92D20] hover:bg-red-50 hover:text-[#D92D20] transition-colors cursor-pointer"
                  onClick={onLogout}
                >
                  <LogOut className="size-4" />
                  <span className="ml-2">Sign out</span>
                </Button>
              </div>
            </Card>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}